import { useActionState, useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, getErrorMessage } from '@/shared/api/api';
import { SwitchCase } from '@/shared/headless';
import { Button, Input, Label } from '@/shared/ui';
import { Text } from '@/shared/ui/text';
import type { SystemNotice } from '../model/schemas';

type FormState = {
  success: boolean;
  message: string;
} | null;

export function EnvironmentSettingsTab() {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationKey: ['update-notification-email', email],
    mutationFn: async ({ notification_email }: { notification_email: string }) => {
      try {
        await api.put('admin/preferences/notification', {
          notification_email,
        });

        return true;
      } catch (error) {
        const errorMessage = await getErrorMessage(error);
        throw new Error(errorMessage);
      }
    },
    onSuccess: async () => {
      toast.success('시스템 공지 수신 이메일이 변경되었습니다.');
      await queryClient.invalidateQueries({ queryKey: ['preferences'] });
      setIsEditing(false);
    },
    onError: async (error) => {
      const errorMessage = await getErrorMessage(error);
      toast.error(errorMessage);
    },
  });

  const { data: querySystemNoticeData } = useQuery<SystemNotice>({
    queryKey: ['preferences'],
    queryFn: async () => await api.get<SystemNotice>('admin/preferences'),
  });

  const updateNotificationEmail = async (
    _prevState: FormState,
    formData: FormData
  ): Promise<FormState> => {
    const notification_email = formData.get('email') as string;

    mutate({ notification_email });

    return {
      success: true,
      message: '시스템 공지 수신 이메일이 변경되었습니다.',
    };
  };

  const [_state, formAction, isPending] = useActionState(updateNotificationEmail, null);

  const handleEdit = useCallback(() => {
    setEmail(querySystemNoticeData?.notification_email || '');
    setIsEditing(true);
  }, [querySystemNoticeData]);

  const handleCancel = useCallback(() => {
    setEmail(querySystemNoticeData?.notification_email || '');
    setIsEditing(false);
  }, [querySystemNoticeData]);

  return (
    <div className="flex flex-col gap-2 shrink-0">
      <Text
        tag="h1"
        typography="typo-title-m-bold"
      >
        시스템 설정
      </Text>

      <form
        action={formAction}
        className="flex flex-col gap-2 mt-6 w-full max-w-[620px]"
      >
        <Label htmlFor="system-notice-email">
          <Text typography="typo-text-xl">공지 및 과금 안내</Text>
        </Label>
        <div className="flex items-center gap-2">
          <Input
            disabled={!isEditing || isPending}
            id="system-notice-email"
            inputMode="email"
            name="email"
            onChange={(e) => setEmail(e.target.value)}
            placeholder="이메일을 입력해주세요."
            type="email"
            value={isEditing ? email : querySystemNoticeData?.notification_email || ''}
          />
          <SwitchCase
            cases={{
              disabled: (
                <Button
                  onClick={handleEdit}
                  type="button"
                  variant={'secondary'}
                >
                  수정
                </Button>
              ),
              enabled: (
                <div className="flex items-center gap-2">
                  <Button
                    disabled={isPending}
                    onClick={handleCancel}
                    type="button"
                    variant={'secondary'}
                  >
                    취소
                  </Button>
                  <Button
                    disabled={isPending}
                    type="submit"
                    variant={'primary'}
                  >
                    {isPending ? '저장 중...' : '저장'}
                  </Button>
                </div>
              ),
            }}
            value={isEditing ? 'enabled' : 'disabled'}
          />
        </div>
      </form>
    </div>
  );
}
